import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FolderKanban, MessageSquareWarning, PhoneCall, Users2, TrendingUp, AlertOctagon, Timer, CheckCircle2, AlertTriangle, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EnRetardBadge, PrioriteBadge } from "@/components/StatusBadge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { useAuth } from "@/context/AuthContext";

const CAT_COLORS = ["#1E3A8A", "#3B82F6", "#8B5CF6", "#F97316", "#EF4444", "#10B981", "#0EA5E9"];

function Kpi({ icon: Icon, label, value, hint, tone = "blue", testid }) {
  const toneMap = {
    blue: "bg-blue-100 text-blue-800",
    orange: "bg-orange-100 text-orange-700",
    red: "bg-red-100 text-red-700",
    emerald: "bg-emerald-100 text-emerald-700",
    violet: "bg-violet-100 text-violet-700",
  };
  return (
    <Card className="border border-slate-200 card-hover animate-in-up bg-white" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</div>
            <div className="mt-2 font-display text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
            {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
          </div>
          <div className={`w-10 h-10 rounded-lg grid place-items-center ${toneMap[tone]}`}>
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [activite, setActivite] = useState([]);
  const [parCat, setParCat] = useState([]);
  const [perf, setPerf] = useState([]);
  const [alertes, setAlertes] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [k, a, c, p, al] = await Promise.all([
          api.get("/dashboard/kpis"),
          api.get("/dashboard/activite-7j"),
          api.get("/dashboard/reclamations-par-categorie"),
          api.get("/dashboard/performance-agents"),
          api.get("/dashboard/alertes-retard", { params: { limit: 5 } }),
        ]);
        setKpis(k.data);
        setActivite(a.data);
        setParCat(c.data.filter((x) => x.categorie));
        setPerf(p.data);
        setAlertes(al.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const exportGlobal = async () => {
    try {
      const res = await api.get("/export/rapport.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "rapport_dkconnect.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={`Bonjour, ${user?.name?.split(" ")[0] || ""}`}
        title="Tableau de bord"
        description="Vue d'ensemble en temps réel de vos dossiers, réclamations et appels."
        testid="dashboard-header"
        actions={
          <Button variant="outline" onClick={exportGlobal} className="rounded-full" data-testid="dash-export-btn">
            <FileDown className="w-4 h-4 mr-2" /> Rapport Excel
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={FolderKanban} label="Dossiers ouverts" value={kpis?.dossiers.ouverts ?? "—"} hint={`${kpis?.dossiers.total ?? 0} au total${kpis?.dossiers.en_retard ? ` · ${kpis.dossiers.en_retard} en retard` : ""}`} tone="blue" testid="kpi-dossiers" />
        <Kpi icon={MessageSquareWarning} label="Réclamations actives" value={kpis?.reclamations.ouvertes ?? "—"} hint={`${kpis?.reclamations.total ?? 0} historiques`} tone="orange" testid="kpi-reclamations" />
        <Kpi icon={AlertTriangle} label="En retard (SLA)" value={kpis?.reclamations.en_retard ?? "—"} hint="Échéance dépassée" tone="red" testid="kpi-retard" />
        <Kpi icon={AlertOctagon} label="Critiques" value={kpis?.reclamations.critiques ?? "—"} hint="Priorité critique (4h)" tone="red" testid="kpi-critiques" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2 border border-slate-200 bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Activité 7 jours</div>
                <CardTitle className="font-display text-xl">Volume par module</CardTitle>
              </div>
              <Badge variant="outline" className="text-slate-500">
                <TrendingUp className="w-3 h-3 mr-1" /> Temps réel
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72" data-testid="activity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activite} margin={{ left: -20, right: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                  <Legend />
                  <Line type="monotone" dataKey="dossiers" name="Dossiers" stroke="#8B5CF6" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="reclamations" name="Réclamations" stroke="#F97316" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="appels" name="Appels" stroke="#3B82F6" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Répartition</div>
            <CardTitle className="font-display text-xl">Réclamations par catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72" data-testid="categorie-chart">
              {parCat.length === 0 ? (
                <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={parCat} dataKey="count" nameKey="categorie" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {parCat.map((entry, i) => (
                        <Cell key={entry.categorie || `cat-${i}`} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Ressources</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Users2 className="w-5 h-5 text-blue-800" /> Volume clients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-display text-5xl font-bold text-slate-900">{kpis?.clients.total ?? "—"}</div>
            <div className="text-slate-500 text-sm mt-2">clients / assurés enregistrés dans la base.</div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Appels</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-blue-800" /> Trafic total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-4">
              <div className="font-display text-5xl font-bold text-slate-900">{kpis?.appels.total ?? "—"}</div>
              <div className="text-red-600 text-sm font-medium">{kpis?.appels.manques ?? 0} manqués</div>
            </div>
            <div className="text-slate-500 text-sm mt-2">Entrants, sortants et manqués cumulés.</div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Top agents</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-800" /> Réclamations résolues
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perf.length === 0 ? (
              <div className="text-slate-400 text-sm">Aucune donnée</div>
            ) : (
              <div className="space-y-3">
                {perf.slice(0, 5).map((p) => (
                  <div key={p.agent} className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-800">{p.agent}</div>
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{p.resolues}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {alertes.length > 0 && (
        <Card className="border border-red-200 bg-red-50/40 mt-6" data-testid="dash-alertes-retard">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-red-700">Alertes SLA</div>
                <CardTitle className="font-display text-xl flex items-center gap-2 text-red-800">
                  <AlertTriangle className="w-5 h-5" /> Réclamations en retard
                </CardTitle>
              </div>
              <EnRetardBadge />
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-red-100">
              {alertes.map((r) => (
                <div key={r.id} className="py-2.5 flex items-center justify-between gap-4" data-testid={`dash-alerte-${r.numero}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-500">{r.numero}</span>
                      <PrioriteBadge value={r.priorite} />
                    </div>
                    <div className="text-sm text-slate-800 truncate mt-0.5">{r.objet}</div>
                  </div>
                  <div className="text-xs font-mono text-red-700 shrink-0">
                    Échéance {new Date(r.echeance).toLocaleString("fr-FR")}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
